import React, { useEffect, useRef, useState } from 'react'
import { trackQuizAnswer, trackQuizShown, trackSubscribeViaEmailCap, trackSubscribeViaSMSCap, trackViewedEmailCap } from 'events/index'
import { stripPhone, validateEmail, validatePhone } from 'utils/validators'
import ModalWrapper from './ModalWrapper'
import { EmailCapture } from './EmailCap'
import { NextRouter, useRouter } from 'next/router'
import { MiniModalProps } from 'emailcaps/MiniModal'
import SMSCap from './SMSCap/SMSCap'
import { Complete, Question, QuestionAbbr, quizData, quizQuestion } from './Complete/Complete'
import { EMAIL_PROMO_QUERY, SMS_PROMO_QUERY } from 'config/url_promos'
import { klaviyoTrackQuizAnswers } from 'events/klaviyo'

export interface SMSError {
	error: boolean
	message: string
}

type Step = 'question' | 'email' | 'complete' | 'phone' | 'final'

const handlePromo = (router: NextRouter) => {
	const email = localStorage.getItem('email')
	const phone = localStorage.getItem('phone')

	if (phone) {
		router.push({
			pathname: router.asPath,
			query: {
				...SMS_PROMO_QUERY.split('&').reduce((acc, curr) => {
					const [key, value] = curr.split('=')
					acc[key] = value
					return acc
				}, {}),
			},
		})
	} else if (email) {
		router.push({
			pathname: router.asPath,
			query: {
				...EMAIL_PROMO_QUERY.split('&').reduce((acc, curr) => {
					const [key, value] = curr.split('=')
					acc[key] = value
					return acc
				}, {}),
			},
		})
	}
}

const QuizWithImage: React.FC<MiniModalProps> = (props) => {
	const router = useRouter()
	const [step, setStep] = useState<Step>('complete')
	const [email, setEmail] = useState('')
	const [loading, setLoading] = useState(false)
	const [phone, setPhone] = useState('')
	const [smsError, setSmsError] = useState<SMSError>({ error: false, message: '' })
	const [answer, setAnswer] = useState<QuestionAbbr>(QuestionAbbr.Low_energy)
	const pageWrapper = useRef(null)
	const captureLocation = props.captureLocation
	const isSmsAB = props.region == 'us' || props.region == ''

	const podhubImageDesktop = 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/summer-campaign-ecap-desktop.jpg'
	const podhubImageMobile = 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/summer-campaign-ecap-mobile.jpg'
	const podhubEye = 'https://eightsleep.imgix.net/assets/podhub_eye_19032024.png'

	const modalName = 'Email Cap - Improve Sleep'

	useEffect(() => {
		if (props.emailCapModalVisible) {
			trackViewedEmailCap(modalName)
		}
	}, [props.emailCapModalVisible])

	const handleClose = () => {
		props.hideEmailCapModal()
		handlePromo(router)
	}

	const handleCloseSMS = () => {
		if (step === 'phone') {
			setStep('final')
		} else {
			handleClose()
		}
	}

	const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(event.target.value)
	}

	const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPhone(event.target.value)
	}

	const emailHandler = async () => {
		const validEmail = validateEmail(email)
		const properties = {
			email: email,
			// answer: answer,
			emailCapType: `2Step - ${modalName}`,
			location: 'React Modal',
			captureLocation: captureLocation,
			image: props.imgUrl,
			description: props.description,
			vanityLink: window.location.href,
			totalDiscount: props.totalDiscount,
			discountCode: props.discountCode,
		}

		if (validEmail) {
			await trackSubscribeViaEmailCap(properties)
		}
	}

	const phoneHandler = async () => {
		const strippedPhone = stripPhone(phone)
		const passesValidation = validatePhone(strippedPhone)

		const properties = {
			email: email,
			phone: '+1' + strippedPhone,
			emailCapType: `2Step - ${modalName}`,
			location: 'React Modal',
			captureLocation: captureLocation,
			image: props.imgUrl,
			description: props.description,
			amount: props.totalDiscount,
			code: props.discountCode,
			region: props.region,
		}

		if (passesValidation) {
			return await trackSubscribeViaSMSCap(properties)
		} else {
			return { error: true, message: 'Wrong phone number format', body: null }
		}
	}

	const emailHandleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		setLoading(true)
		await emailHandler()
		setLoading(false)
		localStorage.setItem('email', email)
		klaviyoTrackQuizAnswers({
			Pain_point_1: answer,
			Quiz_question_type: quizQuestion,
			Snorer: answer === QuestionAbbr.Snore ? 'Y' : '',
		})
		if (isSmsAB) {
			setStep('phone')
		} else {
			setStep('final')
		}
	}

	const phoneHandleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		try {
			event.preventDefault()
			setSmsError({ error: false, message: '' })
			setLoading(true)
			const result = await phoneHandler()
			if (!result.success || result.body?.detail) {
				setSmsError({ error: true, message: result.error || result.body.detail || 'Oops, something is wrong with your phone number.' })
				setLoading(false)
				return
			}
			setLoading(false)

			setStep('final')
			setTimeout(handleClose, 20000)

			localStorage.setItem('phone', phone)
			trackQuizShown()
		} catch (error) {
			setSmsError({ error: true, message: 'Something went wrong, please try again.' })
			setLoading(false)
		}
	}

	const completeHandleSubmit = (abbr: QuestionAbbr) => {
		setStep('email')
		setAnswer(abbr)
		trackQuizAnswer(quizData.get(abbr).question, quizQuestion)
		// trackEmailModalButtonPress(modalName, 'email-continue-button')
		// handleClose()
	}

	const emailCapConfig = {
		title: 'Almost there',
		subheader: 'Unlock your surprise',
		subtitle: `Sign up to receive your surprise and start sleeping better today.`,
		buttonText: `Unlock surprise`,
		dismissText: 'I don’t want a surprise',
	}

	const smsCapConfig = {
		title: 'Limited time',
		subheader: `You've unlocked ${props.totalDiscount} off Pod 4 Ultra`,
		subtitle: `You've unlocked ${props.totalDiscount} off Pod 4 Ultra`,
		description: `Your offer is one click away.`,
		buttonText: `Get offer`,
	}

	const questionConfig = {
		headerText: `Unlock a surprise`,
		hidePill: true,
		title: 'Pod 4 ultra is here',
		subheaderText: '',
	}

	const completeConfig = {
		mysteryEcap: true,
	}

	const blurImage = ['complete', 'email'].includes(step)

	return (
		<ModalWrapper
			pageWrapper={pageWrapper}
			emailCapModalVisible={props.emailCapModalVisible}
			handleClose={handleClose}
			settings={props.settings}
			desktopImg={podhubImageDesktop}
			mobileImg={podhubImageMobile}
			fitbyHeight={true}
			blurred={blurImage}
			overlayImage={blurImage ? podhubEye : ''}
		>
			{step === 'email' && (
				<EmailCapture
					handleEmailChange={handleEmailChange}
					emailHandleSubmit={emailHandleSubmit}
					currency={props.currency}
					closeModal={handleClose}
					title={emailCapConfig.title}
					subheader={emailCapConfig.subheader}
					subtitle={emailCapConfig.subtitle}
					buttonText={loading ? 'Loading...' : emailCapConfig.buttonText}
					dismissText={emailCapConfig.dismissText}
				/>
			)}

			{step === 'phone' && (
				<SMSCap
					handleSMSChange={handlePhoneChange}
					smsHandleSubmit={phoneHandleSubmit}
					currency={props.currency}
					smsError={smsError}
					title={smsCapConfig.title}
					subtitle={smsCapConfig.subtitle}
					buttonText={loading ? 'Loading...' : smsCapConfig.buttonText}
					closeModal={handleCloseSMS}
					description={smsCapConfig.description}
				/>
			)}

			{step === 'complete' && (
				<Question
					title={questionConfig.title}
					headerText={questionConfig.headerText}
					hidePill={questionConfig.hidePill}
					answerSubmit={completeHandleSubmit}
					subheaderText={questionConfig.subheaderText}
				/>
			)}

			{step === 'final' && (
				<Complete
					mysteryEcap={completeConfig.mysteryEcap}
					answer={answer}
				/>
			)}
		</ModalWrapper>
	)
}

export default QuizWithImage
